import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  FEEDBACK_CAPTURE_NOTE_MAX_LENGTH,
  FEEDBACK_MAX_CAPTURES,
  type FeedbackDraftCaptureInput,
  type FeedbackPage,
  type FeedbackScope,
  type FeedbackSubmitInput,
} from "@phoneware/lens-domain";
import { navigate } from "../router.js";
import { useLoad, useSession } from "../session.js";
import { errorText } from "../format.js";
import { Icon } from "../icons.js";
import { Sheet, useToast } from "../ui.js";
import { annotatedScreenshot, capturePage, CaptureEditor, type Mark, type PageCapture, type Tool, type Zoom } from "./request-capture.js";
import { requestPage, useRequestBatch, type ComposeMode } from "./request-batch.js";
export const OpenRequestContext = createContext<(() => void) | null>(null);
export function useOpenRequest(): () => void {
  const open = useContext(OpenRequestContext);
  if (!open) throw new Error("Requests must render inside the Omni shell.");
  return open;
}

/** How the composer captures this page and encodes the annotated image. Browsers use the DOM renderer; tests hand in their own. */
export const CaptureToolsContext = createContext<{ capture: () => Promise<PageCapture>; encode: typeof annotatedScreenshot }>({
  capture: capturePage,
  encode: annotatedScreenshot,
});

const TOOLS: { id: Tool; label: string }[] = [
  { id: "box", label: "Box" }, { id: "arrow", label: "Arrow" },
  { id: "freehand", label: "Freehand" }, { id: "pin", label: "Pin" },
  { id: "element", label: "Select element" },
];

export function RequestComposer({ path, mode = "single", onClose }: { path: string; mode?: ComposeMode; onClose: () => void }): React.ReactElement {
  const { me, api } = useSession();
  const batch = useRequestBatch();
  const tools = useContext(CaptureToolsContext);
  const toast = useToast();
  const batchMode = mode === "batch";
  const listing = useLoad(() => api.feedbackRequests({ limit: 1 }), [api]);
  const choices = !batchMode && (listing.data?.capabilities.chooseScope ?? false);
  const dealers = useLoad(() => choices ? api.dealers() : Promise.resolve([]), [api, choices]);
  const [scopeValue, setScopeValue] = useState("");
  const [comment, setComment] = useState("");
  const [capture, setCapture] = useState<PageCapture | null>(null);
  const [captureBusy, setCaptureBusy] = useState(false);
  const [marks, setMarks] = useState<Mark[]>([]);
  const [tool, setTool] = useState<Tool>("box");
  const [selectedElement, setSelectedElement] = useState<FeedbackPage["selectedElement"]>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState<"" | "submit" | "batch">("");
  const [revision, setRevision] = useState(0);
  const [zoom, setZoom] = useState<Zoom>("fit");
  const lastAttempt = useRef<{ revision: number; input: FeedbackSubmitInput } | null>(null);
  // A retried save sends the same clientCaptureId until the screen, its marks or its note change.
  const lastSave = useRef<{ revision: number; input: FeedbackDraftCaptureInput } | null>(null);
  const opened = useRef(false);
  const textInput = useRef<HTMLTextAreaElement>(null);
  // The object URL is private to this tab and lives only while the composer does.
  useEffect(() => () => { if (capture) URL.revokeObjectURL(capture.url); }, [capture]);

  const scope: FeedbackScope | undefined = !choices ? undefined : scopeValue === "platform" ? { kind: "platform" } : scopeValue.startsWith("dealer:") ? { kind: "dealer", dealerId: scopeValue.slice(7) } : undefined;
  const policy = listing.data?.capturePolicy;
  const ready = listing.data?.capabilities.create && (!choices || !!scope) && (scope?.kind !== "dealer" || !!dealers.data?.some((dealer) => dealer.id === scope.dealerId));
  const full = (batch.draft?.captures.length ?? 0) >= FEEDBACK_MAX_CAPTURES;
  const busy = saving !== "" || captureBusy;
  const canSave = !!capture && !busy && !full && !!policy && !!listing.data?.capabilities.create;
  const close = (): void => { if (!busy) onClose(); };
  const removeCapture = (): void => {
    setCapture(null); setMarks([]); setSelectedElement(null); setZoom("fit"); setRevision((n) => n + 1);
  };
  const startCapture = async (): Promise<void> => {
    setError(""); setCaptureBusy(true);
    // Let the painted loading state appear before the DOM capture starts.
    const frame = Promise.withResolvers<void>();
    requestAnimationFrame(() => frame.resolve());
    await frame.promise;
    try {
      const image = await tools.capture();
      setCapture(image); setMarks([]); setSelectedElement(null); setZoom("fit"); setRevision((n) => n + 1);
    } catch (err) { setError(errorText(err)); }
    finally { setCaptureBusy(false); }
  };
  // Opened from the batch dock, the composer captures this page at once.
  useEffect(() => {
    if (!batchMode || opened.current) return;
    opened.current = true;
    void startCapture();
  }, []);
  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (saving) return;
    const trimmed = comment.trim();
    if (!trimmed || trimmed.length > 4000) {
      setError("Describe the change in Omni."); textInput.current?.focus(); return;
    }
    if (!ready || !policy) return;
    setSaving("submit"); setError("");
    try {
      let input = lastAttempt.current?.revision === revision ? lastAttempt.current.input : null;
      if (!input) {
        input = {
          clientSubmissionId: crypto.randomUUID(), comment: trimmed, page: requestPage(path, capture ? selectedElement : null),
          screenshot: capture ? await tools.encode(capture, marks, policy.maxBytes, policy.contentTypes, policy.maxDimension, policy.maxPixels) : null,
          ...(scope ? { scope } : {}),
        };
        lastAttempt.current = { revision, input };
      }
      const result = await api.submitFeedback(input);
      onClose();
      navigate(`/requests/${encodeURIComponent(result.request.id)}`);
    } catch (err) { setError(errorText(err)); }
    finally { setSaving(""); }
  };
  const saveToBatch = async (): Promise<void> => {
    if (!canSave || !capture || !policy) return;
    // The box's text becomes this screen's note. Lens refuses a longer note rather than cut it, so this checks first.
    const note = comment.trim();
    if (note.length > FEEDBACK_CAPTURE_NOTE_MAX_LENGTH) {
      setError(`A screen's note can be up to ${FEEDBACK_CAPTURE_NOTE_MAX_LENGTH.toLocaleString("en-US")} characters. Shorten it to save this screen.`);
      textInput.current?.focus(); return;
    }
    setSaving("batch"); setError("");
    try {
      let input = lastSave.current?.revision === revision ? lastSave.current.input : null;
      if (!input) {
        input = {
          clientCaptureId: crypto.randomUUID(), page: requestPage(path, selectedElement), note: note || null,
          screenshot: await tools.encode(capture, marks, policy.maxBytes, policy.contentTypes, policy.maxDimension, policy.maxPixels),
        };
        lastSave.current = { revision, input };
      }
      const result = await batch.saveCapture(input);
      onClose();
      toast(`Saved to batch: ${result.draft.captures.length} of ${FEEDBACK_MAX_CAPTURES} screens.`);
    } catch (err) { setError(errorText(err)); }
    finally { setSaving(""); }
  };

  return (
    <Sheet title="Request a change" wide onClose={close} foot={
      <><button type="button" className="btn" disabled={busy} onClick={close}>Cancel</button>
        <button type="button" className={batchMode ? "btn primary" : "btn"} disabled={!canSave} data-testid="request-save-to-batch" onClick={() => { void saveToBatch(); }}>
          <Icon name="layers" />{saving === "batch" ? "Saving to batch" : "Save to batch"}
        </button>
        {batchMode ? null : <button type="submit" form="request-compose-form" className="btn primary" disabled={!ready || busy} data-testid="request-submit">
          {saving === "submit" ? "Submitting request" : "Submit request"}
        </button>}</>
    }>
      <form id="request-compose-form" className="request-compose" onSubmit={(event) => {
        if (!batchMode) { void submit(event); return; }
        event.preventDefault(); void saveToBatch();
      }}>
        <label className="field"><span>{batchMode ? "Note for this screen" : "Describe the change"}</span>
          <textarea ref={textInput} autoFocus className="input" value={comment} maxLength={batchMode ? undefined : 4000} minLength={batchMode ? undefined : 1}
            placeholder={batchMode ? "What should change on this screen?" : "What should change in Omni?"}
            onChange={(event) => { setComment(event.target.value); setRevision((n) => n + 1); }} />
        </label>
        {error || listing.error || dealers.error ? <p role="alert" className="form-error">{error || listing.error?.message || dealers.error?.message}</p> : null}
        {batchMode ? null : choices ? <label className="field"><span>Scope</span><select className="input" aria-label="Scope" value={scopeValue}
          onChange={(event) => { setScopeValue(event.target.value); setRevision((n) => n + 1); }}>
          <option value="">Scope</option><option value="platform">Platform</option>
          {(dealers.data ?? []).map((dealer) => <option key={dealer.id} value={`dealer:${dealer.id}`}>{dealer.name}</option>)}
        </select></label> : me.dealer ? <p className="scope-note">Scope: {me.dealer.name}</p> : null}
        <div><span className="label">{batchMode ? "Screen" : "Screenshot"}</span><p className="shot-title">{batchMode
          ? "Annotate this page, then save it. You describe the whole change when you review the batch."
          : "Optional. Submit one annotated screenshot with this request, or save it to a batch and add screens from other pages."}</p></div>
        {policy ? <p className="scope-note">Screenshots are kept for {policy.retentionDays} days. Request text and history remain afterward.</p> : null}
        {captureBusy ? <div className="request-capture-loading" role="status">Capturing this page</div> : capture ? <div className="request-capture">
          <div className="request-shot-actions">
            <button type="button" className="btn small" onClick={removeCapture}>Remove screenshot</button>
            <button type="button" className="btn small" onClick={() => { setMarks([]); setSelectedElement(null); setRevision((n) => n + 1); }}>Clear annotations</button>
            <button type="button" className="btn small request-zoom" aria-pressed={zoom === "actual"} onClick={() => setZoom(zoom === "actual" ? "fit" : "actual")}>Actual size</button>
          </div>
          <div className="request-tools" role="group" aria-label="Annotation tool">
            {TOOLS.map(({ id, label }) => <button key={id} className="btn small" type="button" aria-pressed={tool === id} onClick={() => setTool(id)}>{label}</button>)}
          </div>
          <CaptureEditor capture={capture} marks={marks} tool={tool} zoom={zoom}
            onMarks={(next) => { setMarks(next); setRevision((n) => n + 1); }}
            onSelection={(next) => { setSelectedElement(next); setRevision((n) => n + 1); }} />
          <p className="shot-title">Draw on the screenshot, or focus it and press Enter to place the selected tool in the middle of the view.{zoom === "actual" ? " Scroll or use the arrow keys to move around." : ""}{batchMode ? "" : " Screenshot optional."}</p>
        </div> : <button className="btn" type="button" onClick={() => { void startCapture(); }}><Icon name="scan-text" />Capture this page</button>}
        {full ? <p className="scope-note">This batch has {FEEDBACK_MAX_CAPTURES} screens, the most one request can carry. Remove one in Review to save another.</p> : null}
        <p className="scope-note">{batchMode ? "Saving a screen starts no work. Phoneware sees the batch only after you submit it." : "Submitted requests wait for Phoneware staff to start agent work."}</p>
      </form>
    </Sheet >
  );
}
